import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Topbar from "@/components/Topbar";
import { toast } from "sonner";
import { Plus, ListChecks } from "@phosphor-icons/react";

const STATUSES = [
  { value: "received", label: "Recebida" },
  { value: "in_analysis", label: "Em análise" },
  { value: "forwarded", label: "Encaminhada" },
  { value: "in_progress", label: "Em andamento" },
  { value: "resolved", label: "Resolvida" },
  { value: "closed_no_action", label: "Encerrada" },
];

/**
 * MandateDemands — demandas do cidadão vinculadas a representantes.
 * Mudar o status aqui dispara o funcionário proativo
 * `mandate_demand_tracker` (via Coordinator) que notifica o autor.
 */
export default function MandateDemands() {
  const [demands, setDemands] = useState([]);
  const [reps, setReps] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({
    customer_id: "", mandate_id: "", subject: "", description: "", channel: "whatsapp",
  });
  const [editing, setEditing] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [note, setNote] = useState("");

  async function reload() {
    setLoading(true);
    try {
      const [{ data: d }, { data: r }, { data: c }] = await Promise.all([
        api.get("/mandate/demands"),
        api.get("/mandate/representatives"),
        api.get("/customers?limit=200"),
      ]);
      setDemands(d?.demands || []);
      setReps(r?.representatives || []);
      setCustomers((c?.customers || c?.items || c || []).filter((x) => !x?.meta?.is_mandate_virtual));
    } catch {
      toast.error("Não foi possível carregar demandas.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { reload(); }, []);

  async function submit() {
    if (!form.customer_id || !form.subject || !form.description) {
      toast.error("Preencha cidadão, assunto e descrição.");
      return;
    }
    try {
      await api.post("/mandate/demands", form);
      toast.success("Demanda registrada.");
      setShowNew(false);
      setForm({ customer_id: "", mandate_id: "", subject: "", description: "", channel: "whatsapp" });
      await reload();
    } catch {
      toast.error("Falha ao registrar.");
    }
  }

  async function changeStatus() {
    if (!editing || !newStatus) return;
    try {
      await api.post(`/mandate/demands/${editing.id}/status`, {
        new_status: newStatus, note: note || null,
      });
      toast.success("Status atualizado. Cidadão será notificado via Coordenador.");
      setEditing(null); setNewStatus(""); setNote("");
      await reload();
    } catch {
      toast.error("Falha ao mudar status.");
    }
  }

  function custName(id) {
    const c = customers.find((x) => x.id === id);
    return c?.name || id;
  }
  function repName(id) {
    const r = reps.find((x) => x.id === id);
    return r?.name || (id ? id : "— sem representante vinculado");
  }

  return (
    <div className="space-y-6" data-testid="mandate-demands-page">
      <Topbar title="Demandas do cidadão" subtitle="Mudanças de status aqui notificam o autor automaticamente, via Coordenador." />
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600">{loading ? "Carregando..." : `${demands.length} demanda(s)`}</div>
        <button
          onClick={() => setShowNew(true)}
          data-testid="mandate-demand-new-btn"
          className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800"
        >
          <Plus size={16} weight="bold" /> Nova demanda
        </button>
      </div>

      {demands.length === 0 && !loading ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
          <ListChecks size={32} className="mx-auto mb-3 text-slate-400" />
          <p className="text-slate-800 font-medium">Nenhuma demanda registrada.</p>
          <p className="text-sm text-slate-500 mt-1">Cada demanda recebe protocolo automático e histórico de status.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2">Protocolo</th>
                <th className="text-left px-4 py-2">Cidadão</th>
                <th className="text-left px-4 py-2">Representante</th>
                <th className="text-left px-4 py-2">Assunto</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {demands.map((d) => (
                <tr key={d.id} data-testid={`mandate-demand-row-${d.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{d.protocolo}</td>
                  <td className="px-4 py-3">{custName(d.customer_id)}</td>
                  <td className="px-4 py-3 text-slate-600">{repName(d.mandate_id)}</td>
                  <td className="px-4 py-3 truncate max-w-xs" title={d.subject}>{d.subject}</td>
                  <td className="px-4 py-3">
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                      {STATUSES.find((s) => s.value === d.status)?.label || d.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => { setEditing(d); setNewStatus(d.status); }}
                      data-testid={`mandate-demand-change-${d.id}`}
                      className="text-xs text-slate-700 hover:text-slate-900 underline"
                    >
                      Mudar status
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal nova demanda */}
      {showNew ? (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-xl rounded-xl bg-white p-6 space-y-3" data-testid="mandate-demand-form-modal">
            <h3 className="text-lg font-semibold text-slate-900">Nova demanda</h3>
            <select
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
              value={form.customer_id}
              onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
              data-testid="mandate-demand-customer-select"
            >
              <option value="">Selecione o cidadão…</option>
              {customers.map((c) => (<option key={c.id} value={c.id}>{c.name || c.phone}</option>))}
            </select>
            <select
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
              value={form.mandate_id}
              onChange={(e) => setForm({ ...form, mandate_id: e.target.value })}
            >
              <option value="">Sem representante específico (institucional)</option>
              {reps.map((r) => (<option key={r.id} value={r.id}>{r.name}</option>))}
            </select>
            <input
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
              placeholder="Assunto"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
            />
            <textarea
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
              rows={4}
              placeholder="Descrição completa da demanda"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
            <div className="flex items-center justify-end gap-2 pt-2">
              <button onClick={() => setShowNew(false)} className="rounded px-3 py-2 text-sm text-slate-600 hover:bg-slate-100">Cancelar</button>
              <button onClick={submit} data-testid="mandate-demand-save-btn" className="rounded bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">Registrar</button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Modal mudar status */}
      {editing ? (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 space-y-3">
            <h3 className="text-lg font-semibold text-slate-900">Atualizar status</h3>
            <div className="text-sm text-slate-600">
              Protocolo <span className="font-mono">{editing.protocolo}</span>
            </div>
            <select
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              data-testid="mandate-demand-status-select"
            >
              {STATUSES.map((s) => (<option key={s.value} value={s.value}>{s.label}</option>))}
            </select>
            <textarea
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
              rows={3}
              placeholder="Observação para o cidadão (opcional, factual)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <div className="flex items-center justify-end gap-2 pt-2">
              <button onClick={() => setEditing(null)} className="rounded px-3 py-2 text-sm text-slate-600 hover:bg-slate-100">Cancelar</button>
              <button onClick={changeStatus} data-testid="mandate-demand-status-save-btn" className="rounded bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">Atualizar e notificar</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
